import queryKeys from "@components/DiscoverFeedInterests/queryKeys";
import { Button, SingleSelectDropdown, TextInput } from "@components/library";
import { COLORS, FEATURE_FLAGS, WINDOW_DIMENSIONS } from "@constants";
import { getDiscoverFeedInterests } from "@requests/discoverFeedInterests";
import { useQuery } from "@tanstack/react-query";
import { getOptionFromValue, getValueFromOption } from "@utils/dropdownUtils";
import { t } from "@utils/i18n";
import { getSignupPrefillData } from "@utils/signupPrefill";
import { isValidURL } from "@utils/textUtils";
import styled from "styled-components";
import * as S from "../StepStyles";
import Step3CompanyScientist from "./CompanyScientist";
import Step3CompanySponsor from "./CompanySponsor";
import Step3ResearchInstitutionAdmin from "./ResearchInstitutionAdmin";
import Step3ResearchInstitutionScientist from "./ResearchInstitutionScientist";

export const COMPANY_SPONSOR_TITLE_LIMIT = 50;

export const HOW_DID_YOU_HEAR_ABOUT_US_OTHER_OPTION = "Other (describe below)";
const HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS = [
  "I received an email from Halo",
  "I received an email from a colleague",
  "I received an email from a university administrator",
  "I found Halo through a search engine",
  "At a conference",
  "Through Linkedin, Meta, or other social media",
  HOW_DID_YOU_HEAR_ABOUT_US_OTHER_OPTION,
].map(getOptionFromValue);

const Step3 = ({
  userType,
  handleOnboard,
  onBack,
  profileInfo,
  setProfileInfo,
  user,
  isSubmitting,
}) => {
  const {
    company,
    role,
    title,
    companyName,
    companyURL,
    country_code,
    disciplines,
    areasOfExpertise,
    university,
    universityHasCountry,
    howDidYouHearAboutUs,
    selectedCompanyId,
  } = profileInfo;

  const isCompanySponsor = userType === "company-sponsor";
  // Get prefill data for institution name (used as company name for sponsors)
  const prefillData = getSignupPrefillData();
  const prefillInstitutionName = prefillData.institutionName;

  const {
    data: discoverFeedInterestsData,
    isLoading: isDiscoverFeedInterestsLoading,
    isError: isDiscoverFeedInterestsError,
  } = useQuery({
    queryKey: queryKeys.getDiscoverFeedInterestsQueryKey(),
    queryFn: getDiscoverFeedInterests,
    enabled: isCompanySponsor,
  });

  const discoverFeedInterestCount = discoverFeedInterestsData?.discover_feed_interests?.length ?? 0;

  const shouldShowDiscoverFeedInterestValidation =
    isCompanySponsor &&
    !isDiscoverFeedInterestsLoading &&
    !isDiscoverFeedInterestsError &&
    discoverFeedInterestCount === 0;

  let disabled = isSubmitting;
  if (isCompanySponsor) {
    const hasRequiredProfileFields =
      company && title && title.length <= COMPANY_SPONSOR_TITLE_LIMIT;

    const hasRequiredDiscoverFeedInterests =
      !isDiscoverFeedInterestsLoading &&
      !isDiscoverFeedInterestsError &&
      discoverFeedInterestCount > 0;

    disabled ||= !(hasRequiredProfileFields && hasRequiredDiscoverFeedInterests);
  }
  if (userType === "company-scientist")
    if (FEATURE_FLAGS.REPLACE_STARTUP_WITH_COMPANY) {
      disabled ||= !(selectedCompanyId
        ? selectedCompanyId && role && disciplines?.length && areasOfExpertise?.length
        : companyName &&
          isValidURL(companyURL) &&
          country_code &&
          role &&
          disciplines?.length &&
          areasOfExpertise?.length);
    } else {
      disabled ||= !(
        companyName &&
        isValidURL(companyURL) &&
        country_code &&
        role &&
        disciplines?.length &&
        areasOfExpertise?.length
      );
    }
  if (userType === "research-administrator")
    disabled ||= !(university && disciplines?.length && (universityHasCountry || country_code));
  if (userType === "research-scientist")
    disabled ||= !(
      university &&
      role &&
      disciplines?.length &&
      areasOfExpertise?.length &&
      (universityHasCountry || country_code)
    );

  const onContinue = () => {
    if (userType === "company-sponsor") return handleOnboard("sponsor");
    if (userType === "research-administrator") return handleOnboard("admin");
    return handleOnboard("scientist");
  };

  const stepSubheading = "Enter profile information";
  let stepHeading = "";
  switch (userType) {
    case "research-scientist":
    case "company-scientist":
      stepHeading =
        "Create a profile to start offering your expertise.\nYou can add more information later.";
      break;
    case "company-sponsor":
      stepHeading = "Create a profile to start finding solutions and experts.";
      break;
    case "research-administrator":
      stepHeading =
        "Create a profile to start connecting faculty with relevant partnering requests.";
      break;
  }

  return (
    <Container center>
      <S.StepContainer width="592px">
        <S.StepHeader subheading={stepSubheading} heading={stepHeading} />
        {isCompanySponsor && (
          <Step3CompanySponsor
            profileInfo={profileInfo}
            setProfileInfo={setProfileInfo}
            email={user.email}
            prefillCompanyName={prefillInstitutionName}
            isPrefillReadonly={false}
            shouldShowDiscoverFeedInterestValidation={shouldShowDiscoverFeedInterestValidation}
          />
        )}
        {userType === "company-scientist" && (
          <Step3CompanyScientist
            profileInfo={profileInfo}
            setProfileInfo={setProfileInfo}
            prefillCompanyName={prefillInstitutionName}
          />
        )}
        {userType === "research-administrator" && (
          <Step3ResearchInstitutionAdmin
            profileInfo={profileInfo}
            setProfileInfo={setProfileInfo}
            prefillInstitutionName={prefillInstitutionName}
            isPrefillReadonly={false}
          />
        )}
        {userType === "research-scientist" && (
          <Step3ResearchInstitutionScientist
            profileInfo={profileInfo}
            setProfileInfo={setProfileInfo}
            prefillInstitutionName={prefillInstitutionName}
            isPrefillReadonly={false}
          />
        )}
        <S.DropdownRow>
          <S.DropdownTitle>
            To help us improve, tell us how you initially heard about Halo.
          </S.DropdownTitle>
          <S.DropdownSubtitle>Optional</S.DropdownSubtitle>
          <SingleSelectDropdown
            placeholder={t("pages.onboarding_container.how_did_you_hear_about_us.placeholder")}
            placeholderColor={COLORS.NEUTRAL_400}
            options={HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS}
            onChange={(option) =>
              setProfileInfo({ ...profileInfo, howDidYouHearAboutUs: getValueFromOption(option) })
            }
            value={getOptionFromValue(howDidYouHearAboutUs)}
          />
          {howDidYouHearAboutUs === HOW_DID_YOU_HEAR_ABOUT_US_OTHER_OPTION && (
            <>
              <br />
              <TextInput
                placeholder="Please describe"
                placeholderColor={COLORS.NEUTRAL_400}
                value={profileInfo.howDidYouHearAboutUsOther}
                onChange={(e) => {
                  setProfileInfo({ ...profileInfo, howDidYouHearAboutUsOther: e.target.value });
                }}
                hideClearButton
              />
            </>
          )}
        </S.DropdownRow>
        <S.Hairline />
        <S.ButtonContainer>
          <Button variant="ghost" width="120px" onClick={() => onBack("step2")}>
            Back
          </Button>
          <Button
            disabled={disabled}
            onClick={onContinue}
            data-testid="company-sponsor-step-4-submit-button"
          >
            Complete signup
          </Button>
        </S.ButtonContainer>
      </S.StepContainer>
    </Container>
  );
};

export default Step3;
const Container = styled.div`
  text-align: left;
  padding: 0;
  margin-bottom: 200px;
  ${({ center }) => !center && `width: 800px;`}

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    padding: 0 30px;
    width: 100%;
  }
`;
